// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { NavLink, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { Field, reduxForm, type FormProps } from 'redux-form';

import { getCatalogs, getLicensedFeatures, getSites } from 'state/selectors';
import type {
  Catalog,
  Dispatch,
  LicensedFeatures,
  NetPublishSite,
} from 'state/types';
import {
  REPORT_ORGANIZER_TYPE_CATALOGS,
  REPORT_ORGANIZER_TYPE_NP_SITES,
  type ReportData,
} from 'state/types/reportTypes';
import { getCatalogs as getCatalogsAction } from 'state/commands/catalogCommands';
import { getSites as getSitesAction } from 'state/commands/netPublishCommands';
import { downloadReport, viewReport } from 'state/commands/reportCommands';
import localizedStrings from 'util/localizedStrings';
import { reportFormConfigs } from 'util/reportUtils';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import LabeledField from 'view/Forms/LabeledField';

type Props = {
  catalogs: Catalog[],
  downloadReport: typeof downloadReport,
  getCatalogsAction: typeof getCatalogsAction,
  getSitesAction: typeof getSitesAction,
  intl: IntlShape,
  licensedFeatures: LicensedFeatures,
  sites: NetPublishSite[],
  viewReport: typeof viewReport,
} & FormProps;

const ALL_CATALOGS = 'All Catalogs';
const ALL_SITES = 'All Sites';

class ReportsPage extends Component<Props> {
  getCurrentReportFormConfig: Function;
  getDefaultDateValues: Function;
  getReportType: Function;
  initializeForm: Function;
  submit: Function;

  constructor() {
    super();
    this.initializeForm = this.initializeForm.bind(this);
    this.getCurrentReportFormConfig = this.getCurrentReportFormConfig.bind(
      this
    );
    this.getDefaultDateValues = this.getDefaultDateValues.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this.props.getCatalogsAction();
    if (this.props.licensedFeatures && this.props.licensedFeatures.np) {
      this.props.getSitesAction();
    }
    this.initializeForm();
  }

  getDefaultDateValues() {
    const startingDate = new Date();
    startingDate.setMonth(startingDate.getMonth() - 3);
    return {
      endingDate: new Date().toISOString().substr(0, 10),
      startingDate: startingDate.toISOString().substr(0, 10),
    };
  }

  initializeForm() {
    const reportFormConfig = this.getCurrentReportFormConfig();
    const organizerType = reportFormConfig && reportFormConfig.organizerType;

    this.props.initialize({
      catalogName:
        organizerType &&
        organizerType === REPORT_ORGANIZER_TYPE_CATALOGS &&
        ALL_CATALOGS,
      siteName:
        organizerType &&
        organizerType === REPORT_ORGANIZER_TYPE_NP_SITES &&
        ALL_SITES,
      ...this.getDefaultDateValues(),
    });
  }

  getCurrentReportFormConfig() {
    const pathSegments = this.props.location.pathname.split('/');
    const reportName = pathSegments[pathSegments.length - 1];
    const reportFormConfig = reportFormConfigs.find(
      rf => rf.name === reportName
    );
    return reportFormConfig;
  }

  submit(values) {
    const catalog = this.props.catalogs.find(
      c => c.name === values.catalogName
    );
    const site =
      values.site && this.props.sites.find(s => s.siteName === values.site);
    const catalogId = catalog && catalog.catalogId;
    const reportFormConfig = this.getCurrentReportFormConfig();
    const reportData: ReportData = {
      catalogName: values.catalogName,
      catalogId: catalogId,
      endingDate: values.endingDate,
      isDownload: values.isDownload,
      name: reportFormConfig && reportFormConfig.name,
      siteId: site && site.siteId,
      startingDate: values.startingDate,
      viewName: reportFormConfig && reportFormConfig.viewName,
    };

    if (values.isDownload) {
      this.props.downloadReport(reportData);
    } else {
      this.props.viewReport(reportData);
    }
  }

  renderForm(reportConfig) {
    const {
      catalogs,
      intl: { formatMessage },
      handleSubmit,
      invalid,
      sites,
    } = this.props;
    return (
      <div className="column is-two-thirds page">
        <div className="page-header">
          <FormattedMessage {...reportConfig.label} />
        </div>
        <div className="page-contents-textured portfolio-form-container">
          <div className="box">
            <form
              style={{ width: '700px' }}
              onSubmit={handleSubmit(this.submit)}
              data-test-name="report-form"
            >
              {reportConfig.organizerType ===
                REPORT_ORGANIZER_TYPE_CATALOGS && (
                <LabeledField label={formatMessage(localizedStrings.CATALOG)}>
                  <div className="select">
                    <Field
                      className="select"
                      component="select"
                      id="catalogName"
                      name="catalogName"
                      style={{ width: '400px' }}
                      type="select"
                    >
                      <option key={ALL_CATALOGS} value={ALL_CATALOGS}>
                        {formatMessage(localizedStrings.ALL_CATALOGS)}
                      </option>
                      {catalogs.map(catalog => (
                        <option key={catalog.name} value={catalog.name}>
                          {catalog.name}
                        </option>
                      ))}
                    </Field>
                  </div>
                </LabeledField>
              )}

              {reportConfig.organizerType ===
                REPORT_ORGANIZER_TYPE_NP_SITES && (
                <LabeledField label={formatMessage(localizedStrings.SITE)}>
                  <div className="select">
                    <Field
                      className="select"
                      component="select"
                      id="site"
                      name="site"
                      style={{ width: '400px' }}
                      type="select"
                    >
                      <option key={ALL_SITES} value={ALL_SITES}>
                        {formatMessage(localizedStrings.ALL_SITES)}
                      </option>
                      {sites.map(site => (
                        <option key={site.siteId}>{site.siteName}</option>
                      ))}
                    </Field>
                  </div>
                </LabeledField>
              )}

              {reportConfig.hasDateRange && (
                <Fragment>
                  <div className="vertical-spacer-large" />

                  <LabeledField label={formatMessage(localizedStrings.FROM)}>
                    <Field
                      className="date"
                      component="input"
                      id="startingDate"
                      name="startingDate"
                      style={{ fontSize: '15px', marginTop: '4px' }}
                      type="date"
                    />
                  </LabeledField>
                  <LabeledField label={formatMessage(localizedStrings.TO)}>
                    <Field
                      className="date"
                      component="input"
                      id="endingDate"
                      name="endingDate"
                      style={{ fontSize: '15px', marginTop: '4px' }}
                      type="date"
                    />
                  </LabeledField>
                </Fragment>
              )}

              <div className="vertical-spacer-large" />

              <LabeledField label="">
                <div className="field is-grouped">
                  <div className="control">
                    <button
                      className="button"
                      data-test-name="download-report"
                      disabled={invalid}
                      type="button"
                      onClick={handleSubmit(values =>
                        this.submit({
                          ...values,
                          isDownload: true,
                        })
                      )}
                    >
                      <FormattedMessage {...localizedStrings.DOWNLOAD_REPORT} />
                    </button>
                  </div>
                  <div className="control">
                    <button
                      className="button is-primary"
                      data-test-name="view-report"
                      disabled={invalid}
                      type="submit"
                    >
                      <FormattedMessage {...localizedStrings.VIEW_REPORT} />
                    </button>
                  </div>
                </div>
              </LabeledField>
            </form>
            <div className="vertical-spacer-xlarge" />
            {reportConfig.description && (
              <FormattedMessage {...reportConfig.description} />
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      intl: { formatMessage },
    } = this.props;
    return (
      <div className="page" data-test-name="reports-page">
        <div className="page-contents columns is-gapless">
          <div className="column is-one-third page">
            <div className="page-header">
              {formatMessage(localizedStrings.NAV_REPORTING)}
            </div>
            <div className="page bordered-page-column is-scrollable">
              <table className="table is-fullwidth is-hoverable">
                <tbody>
                  {reportFormConfigs.map(reportConfig => (
                    <tr key={reportConfig.name}>
                      <td
                        className="no-padding"
                        data-test-name={'report-select-' + reportConfig.name}
                      >
                        <NavLink
                          className="list-item nav-item-list"
                          activeClassName="nav-item-list-selected"
                          to={`/reports/${reportConfig.name}`}
                        >
                          <FormattedMessage {...reportConfig.label} />
                        </NavLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Switch>
            <Redirect
              exact
              from="/reports"
              to={`reports/${reportFormConfigs[0].name}`}
            />
            {reportFormConfigs.map(reportConfig => (
              <Route
                key={reportConfig.name}
                path={`/reports/${reportConfig.name}`}
                render={props => this.renderForm(reportConfig)}
              />
            ))}
          </Switch>
        </div>
      </div>
    );
  }
}

function validate(values) {
  const errors = {};
  if (
    values.startingDate &&
    values.endingDate &&
    values.startingDate > values.endingDate
  ) {
    errors.startingDate = 'Invalid';
    errors.endingDate = 'Invalid';
  }
  return errors;
}

const reportForm = reduxForm({
  form: 'ReportForm',
  validate,
})(injectIntl(ReportsPage));

const reportsPageWrapper = connect(
  state => ({
    catalogs: getCatalogs(state),
    licensedFeatures: getLicensedFeatures(state),
    sites: getSites(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      downloadReport,
      getCatalogsAction,
      getSitesAction,
      viewReport,
    })
)(reportForm);

export default withRouter(reportsPageWrapper);



// WEBPACK FOOTER //
// ./src/view/Pages/ReportsPage.js